import type { SorenessArea, SymptomCodeName } from '@cadencia/contracts';
import { computeReadiness, SYMPTOM_CODES, SYMPTOM_LABELS } from '@cadencia/domain';
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EscalaInput } from '../../components/EscalaInput.js';
import { MapaCorporal } from '../../components/MapaCorporal.js';
import { Boton, BotonEnlace, Cabecera, Estado, Tarjeta } from '../../components/ui.js';
import { useAppData } from '../../data/DataProvider.js';
import { saveReadinessCheckin } from '../../data/repositories/readinessRepo.js';
import { currentSafetyState } from '../../data/services/safetyService.js';
import './entrenar.css';
import { mensajeDeError } from '../../lib/errores.js';

/**
 * Check-in de readiness (requisito §15): 10-15 segundos, una sola pantalla.
 * Escalas coherentes: energía y motivación 5 = mejor; estrés y agujetas
 * 5 = máximo. El motor invierte el signo internamente.
 */
export function ReadinessPage(): ReactNode {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { ctx, ready, refreshPending } = useAppData();
  const navigate = useNavigate();

  const [sleepHours, setSleepHours] = useState(7.5);
  const [sleepQuality, setSleepQuality] = useState(3);
  const [energy, setEnergy] = useState(3);
  const [motivation, setMotivation] = useState(3);
  const [stress, setStress] = useState(3);
  const [soreness, setSoreness] = useState(2);
  const [areas, setAreas] = useState<SorenessArea[]>([]);
  const [mostrarMapa, setMostrarMapa] = useState(false);
  const [sintomas, setSintomas] = useState<SymptomCodeName[]>([]);
  // El dolor articular se localiza en su propio mapa: sin dónde, no se puede
  // acotar qué no cargar (encargo del 23/09/2026, §8).
  const [dolores, setDolores] = useState<SorenessArea[]>([]);
  // Posible urgencia tras guardar: NO se pasa a la sesión.
  const [urgencia, setUrgencia] = useState<string[] | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!ready || !ctx) return <Estado tipo="cargando" titulo="Cargando" />;

  const score = computeReadiness({
    sleepHours,
    sleepQuality,
    energy,
    motivation,
    stress,
    soreness,
  });

  const dolorArticular = sintomas.includes('joint_pain');

  const continuar = (): void => {
    if (dolorArticular && dolores.length === 0) {
      setError('Señala en el mapa qué articulación te duele: sin eso no puedo saber qué no cargar.');
      return;
    }
    setGuardando(true);
    setError(null);
    void (async () => {
      try {
        await saveReadinessCheckin(ctx, {
          sleepHours,
          sleepQuality,
          energy,
          motivation,
          stress,
          soreness,
          // Las agujetas y el dolor viajan juntos; el dominio los distingue por
          // `kind` y cuenta cada región una vez por check-in.
          sorenessAreas: dolorArticular ? [...areas, ...dolores] : areas,
          reportedSymptoms: sintomas,
        });
        await refreshPending();
        // La seguridad se mira AQUÍ, antes de la sesión: ante una posible
        // urgencia no hay sesión que ofrecer, ni siquiera sin cargas.
        const seguridad = await currentSafetyState(ctx.userId);
        if (seguridad.urgent) {
          setUrgencia(seguridad.flags.filter((f) => f.urgent).map((f) => f.message));
          setGuardando(false);
          return;
        }
        navigate(`/entrenar/sesion/${sessionId}/activa`);
      } catch (caught) {
        setError(mensajeDeError(caught));
        setGuardando(false);
      }
    })();
  };

  if (urgencia !== null) {
    return (
      <>
        <Cabecera titulo="Hoy no se entrena" />
        <section className="aviso-seguridad" role="alert">
          {urgencia.map((mensaje) => (
            <p key={mensaje} className="aviso-seguridad__texto">
              {mensaje}
            </p>
          ))}
          <p className="aviso-seguridad__texto">
            La aplicación no valora síntomas. Tu plan sigue ahí: cuando te hayan visto, vuelve a hacer
            el check-in.
          </p>
        </section>
        <div className="separado">
          <BotonEnlace a="/entrenar" variante="secundario">
            Volver
          </BotonEnlace>
        </div>
      </>
    );
  }

  return (
    <>
      <Cabecera
        titulo="¿Cómo estás hoy?"
        descripcion="Diez segundos. Ajusta la sesión de hoy, no tu planificación."
        volver={{ a: `/entrenar/sesion/${sessionId}`, etiqueta: 'La sesión' }}
      />

      <Tarjeta>
        <div className="campo">
          <span className="campo__etiqueta">Horas de sueño</span>
          <div className="registro__control">
            <button
              type="button"
              className="registro__paso"
              aria-label="Media hora menos"
              onClick={() => setSleepHours((valor) => Math.max(0, valor - 0.5))}
            >
              −
            </button>
            <output className="registro__input registro__input--lectura">
              {sleepHours.toLocaleString('es-ES', { minimumFractionDigits: 1 })} h
            </output>
            <button
              type="button"
              className="registro__paso"
              aria-label="Media hora más"
              onClick={() => setSleepHours((valor) => Math.min(16, valor + 0.5))}
            >
              +
            </button>
          </div>
        </div>

        <EscalaInput
          etiqueta="Calidad del sueño"
          valor={sleepQuality}
          onChange={setSleepQuality}
          extremoBajo="Muy mala"
          extremoAlto="Muy buena"
        />
        <EscalaInput
          etiqueta="Energía"
          valor={energy}
          onChange={setEnergy}
          extremoBajo="Sin nada"
          extremoAlto="A tope"
        />
        <EscalaInput
          etiqueta="Motivación"
          valor={motivation}
          onChange={setMotivation}
          extremoBajo="Ninguna"
          extremoAlto="Máxima"
        />
        <EscalaInput
          etiqueta="Estrés"
          valor={stress}
          onChange={setStress}
          extremoBajo="Nada"
          extremoAlto="Muchísimo"
        />
        <EscalaInput
          etiqueta="Agujetas"
          valor={soreness}
          onChange={setSoreness}
          extremoBajo="Ninguna"
          extremoAlto="Muchísimas"
        />

        <fieldset className="sintomas">
          <legend className="campo__etiqueta">¿Algo que deba saber?</legend>
          <p className="campo__pista">
            Marca solo lo que notes hoy. Los cuatro primeros detienen el entrenamiento y te digo qué
            hacer; el dolor articular, lo que carga esa zona. La aplicación no valora síntomas.
          </p>
          {SYMPTOM_CODES.map((codigo) => (
            <label key={codigo} className="sintomas__opcion">
              <input
                type="checkbox"
                checked={sintomas.includes(codigo)}
                onChange={(evento) =>
                  setSintomas((previo) =>
                    evento.target.checked
                      ? [...previo, codigo]
                      : previo.filter((marcado) => marcado !== codigo),
                  )
                }
              />
              {SYMPTOM_LABELS[codigo]}
            </label>
          ))}
        </fieldset>

        {dolorArticular && (
          <div className="separado">
            <p className="campo__etiqueta">¿Dónde te duele?</p>
            <MapaCorporal tipo="discomfort" seleccion={dolores} onChange={setDolores} />
          </div>
        )}

        {mostrarMapa ? (
          <MapaCorporal seleccion={areas} onChange={setAreas} />
        ) : (
          <Boton variante="texto" onClick={() => setMostrarMapa(true)}>
            Marcar zonas con agujetas (opcional)
          </Boton>
        )}
      </Tarjeta>

      <p className="campo__pista separado">
        Readiness estimado:{' '}
        <strong className="numeros">{score.toLocaleString('es-ES', { maximumFractionDigits: 1 })}</strong>
        /100
      </p>

      <Boton ancho="completo" onClick={continuar} disabled={guardando}>
        {guardando ? 'Guardando…' : 'Continuar'}
      </Boton>

      {error !== null && (
        <p className="campo__error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
